import "@/i18n";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import RecaptchaProvider from "@/components/RecaptchaProvider";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useLanguageSEO from "@/hooks/useLanguageSEO";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import Index from "./pages/Index";
import About from "./pages/About";
import Talent from "./pages/Talent";
import TalentProfile from "./pages/TalentProfile";
import ForTalent from "./pages/ForTalent";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminAuth from "./pages/admin/AdminAuth";
import Dashboard from "./pages/admin/Dashboard";
import TalentAdmin from "./pages/admin/TalentAdmin";
import ClientsAdmin from "./pages/admin/ClientsAdmin";
import PagesAdmin from "./pages/admin/PagesAdmin";
import InboxAdmin from "./pages/admin/InboxAdmin";
import UsersAdmin from "./pages/admin/UsersAdmin";
import SignInLogsAdmin from "./pages/admin/SignInLogsAdmin";
import SpecialtiesAdmin from "./pages/admin/SpecialtiesAdmin";

const queryClient = new QueryClient();

// Wrapper component to sync URL language with i18n and manage SEO
const LanguageWrapper = ({ children }: { children: React.ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Add hreflang and canonical tags
  useLanguageSEO();

  useEffect(() => {
    if (lang && (lang === "en" || lang === "fr") && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return <>{children}</>;
};

// Routes with language prefix
const LocalizedRoutes = () => (
  <LanguageWrapper>
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/about" element={<About />} />
      <Route path="/talent" element={<Talent />} />
      <Route path="/talent/:id" element={<TalentProfile />} />
      <Route path="/for-talent" element={<RecaptchaProvider><ForTalent /></RecaptchaProvider>} />
      <Route path="/services" element={<Services />} />
      <Route path="/contact" element={<RecaptchaProvider><Contact /></RecaptchaProvider>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </LanguageWrapper>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <ThemeProvider>
          
            <BrowserRouter>
              <ScrollToTop />
              <Toaster />
              <Sonner />
              <Routes>
                {/* Redirect root to default language */}
                <Route path="/" element={<Navigate to="/en" replace />} />
                
                {/* Admin auth */}
                <Route path="/admin/login" element={<AdminAuth />} />
                
                {/* Admin routes (protected) */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="talent" element={<TalentAdmin />} />
                  <Route path="talent/specialties" element={<SpecialtiesAdmin />} />
                  <Route path="clients" element={<ClientsAdmin />} />
                  <Route path="pages" element={<PagesAdmin />} />
                  <Route path="inbox" element={<InboxAdmin />} />
                  <Route path="users" element={<UsersAdmin />} />
                  <Route path="logs" element={<SignInLogsAdmin />} />
                </Route>
                
                {/* Language-prefixed routes */}
                <Route path="/en/*" element={<LocalizedRoutes />} />
                <Route path="/fr/*" element={<LocalizedRoutes />} />
                
                {/* Catch-all for non-prefixed routes - redirect to English version */}
                <Route path="/about" element={<Navigate to="/en/about" replace />} />
                <Route path="/talent" element={<Navigate to="/en/talent" replace />} />
                <Route path="/talent/:id" element={<Navigate to="/en/talent/:id" replace />} />
                <Route path="/services" element={<Navigate to="/en/services" replace />} />
                <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
                
                {/* Final catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          
        </ThemeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
